// Ported from Studio Playground frontend/src/components/booth/PizzulMoves.jsx.
import React, { useEffect, useRef, useState } from 'react';
import { Copy, Undo2, X } from 'lucide-react';
import { buildAssistBody } from '@/lyrics/boothAssist';
import {
  BoothClientError,
  boothFetch,
  explainError,
  shouldRetryPass,
} from '@/lyrics/boothClient';
import { copyText } from '@/lyrics/copyText';
import { lastWord, swapLastWord } from '@/lyrics/editorHelpers';

const AI_OK_KEY = 'ba_booth_ai_ok';
const PASS_KEY = 'ba_booth_pass';
const TURNSTILE_SCRIPT_ID = 'ba-booth-turnstile';

let turnstileScriptPromise = null;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;
  turnstileScriptPromise = new Promise((resolve, reject) => {
    const finish = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Quick check did not load.'));
    };
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });
  return turnstileScriptPromise;
};

const readFlag = () => {
  try {
    return localStorage.getItem(AI_OK_KEY) === '1';
  } catch (_error) {
    return false;
  }
};

const readPass = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PASS_KEY) || 'null');
    return parsed?.token ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const writePass = (value) => {
  try {
    if (value) sessionStorage.setItem(PASS_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(PASS_KEY);
  } catch (_error) {
    // A pass can still be used in component memory for this visit.
  }
};

const focusedSection = (sheet, focus) => (sheet.sections || []).find((section) => (
  (focus?.sectionId && section.id === focus.sectionId)
  || (focus?.markerId && section.marker_id === focus.markerId)
));

const PizzulMoves = ({ config, focusedLine, onCare, onReplaceLine, sheet }) => {
  const [disclosed, setDisclosed] = useState(readFlag);
  const [disclosureMove, setDisclosureMove] = useState(null);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [challengeError, setChallengeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refusal, setRefusal] = useState(null);
  const [result, setResult] = useState(null);
  const [rhymeOpen, setRhymeOpen] = useState(false);
  const [targetWord, setTargetWord] = useState('');
  const [meaning, setMeaning] = useState('');
  const [leftToday, setLeftToday] = useState(config?.moves_left_today);
  const [undo, setUndo] = useState(null);
  const [copied, setCopied] = useState('');
  const passRef = useRef(readPass());
  const challengeRef = useRef(null);
  const challengePromiseRef = useRef(null);
  const undoTimer = useRef(null);
  const copyTimer = useRef(null);

  const section = focusedSection(sheet, focusedLine);
  const focusedText = section?.lines?.[focusedLine?.lineIndex]?.text || '';
  const available = Boolean(config?.ai_available && config?.turnstile_site_key && !refusal);
  const disabledCaption = !available
    ? (refusal
      ? explainError(refusal.status, refusal.detail)
      : explainError(503, config?.ai_reason || 'resting'))
    : '';

  useEffect(() => {
    setLeftToday(config?.moves_left_today);
  }, [config?.moves_left_today]);

  useEffect(() => () => {
    clearTimeout(undoTimer.current);
    clearTimeout(copyTimer.current);
    challengePromiseRef.current?.reject(new Error('Quick check closed.'));
  }, []);

  useEffect(() => {
    if (!challengeOpen || !challengeRef.current) return undefined;
    let widgetId;
    let live = true;
    loadTurnstile()
      .then((turnstile) => {
        if (!live || !challengeRef.current) return;
        widgetId = turnstile.render(challengeRef.current, {
          sitekey: config.turnstile_site_key,
          action: 'booth',
          appearance: 'interaction-only',
          callback: (token) => {
            challengePromiseRef.current?.resolve(token);
            challengePromiseRef.current = null;
            setChallengeOpen(false);
          },
          'error-callback': () => {
            challengePromiseRef.current?.reject(new Error('Quick check failed. Try again.'));
            challengePromiseRef.current = null;
            setChallengeError('Quick check failed. Try again.');
          },
          'expired-callback': () => {
            challengePromiseRef.current?.reject(new Error('Quick check expired. Try again.'));
            challengePromiseRef.current = null;
            setChallengeError('Quick check expired. Try again.');
          },
        });
      })
      .catch(() => {
        challengePromiseRef.current?.reject(new Error('Quick check did not load. Try again.'));
        challengePromiseRef.current = null;
        setChallengeError('Quick check did not load. Try again.');
      });
    return () => {
      live = false;
      if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [challengeOpen, config?.turnstile_site_key]);

  const requestChallenge = () => new Promise((resolve, reject) => {
    challengePromiseRef.current = { resolve, reject };
    setChallengeError('');
    setChallengeOpen(true);
  });

  const getPass = async () => {
    const turnstileToken = await requestChallenge();
    const response = await boothFetch('pass', {
      method: 'POST',
      body: { turnstile_token: turnstileToken },
    });
    const next = { token: response.pass, moves_left: response.moves };
    passRef.current = next;
    writePass(next);
    return next;
  };

  const sendAssist = async (body, retried = false) => {
    const currentPass = passRef.current?.token ? passRef.current : await getPass();
    try {
      const response = await boothFetch('assist', {
        method: 'POST',
        body,
        pass: currentPass.token,
      });
      const movesLeft = Math.max(0, Number(currentPass.moves_left ?? config.pass_moves) - 1);
      passRef.current = { ...currentPass, moves_left: movesLeft };
      writePass(passRef.current);
      return response;
    } catch (requestError) {
      if (shouldRetryPass(requestError.status, requestError.detail, retried)) {
        passRef.current = null;
        writePass(null);
        await getPass();
        return sendAssist(body, true);
      }
      throw requestError;
    }
  };

  const runMove = async (move, extra = {}) => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const body = buildAssistBody(sheet, focusedLine, move, extra);
      const response = await sendAssist(body);
      if (response.care) {
        onCare(response.care);
        return;
      }
      setResult({ ...response, move });
      if (Number.isFinite(Number(response.left_today))) {
        setLeftToday(Number(response.left_today));
      }
    } catch (requestError) {
      if (requestError instanceof BoothClientError && (
        requestError.status === 503
        || (requestError.status === 429 && requestError.detail === 'daily_limit')
      )) {
        setRefusal({ status: requestError.status, detail: requestError.detail });
      }
      setError(requestError instanceof BoothClientError
        ? requestError.message : 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const beginMove = (move, extra = {}) => {
    if (!available || loading) return;
    if (!disclosed) {
      setDisclosureMove({ move, extra });
      return;
    }
    runMove(move, extra);
  };

  const acceptDisclosure = () => {
    try {
      localStorage.setItem(AI_OK_KEY, '1');
    } catch (_error) {
      // The in-memory flag avoids repeating the card during this visit.
    }
    setDisclosed(true);
    const pending = disclosureMove;
    setDisclosureMove(null);
    if (pending?.move === 'open_rhymes') {
      setTargetWord(lastWord(focusedText));
      setMeaning('');
      setRhymeOpen(true);
    } else if (pending) {
      runMove(pending.move, pending.extra);
    }
  };

  const openRhymes = () => {
    if (!available || loading) return;
    if (!disclosed) {
      setDisclosureMove({ move: 'open_rhymes', extra: {} });
      return;
    }
    setTargetWord(lastWord(focusedText));
    setMeaning('');
    setRhymeOpen(true);
  };

  const markCopied = (value) => {
    setCopied(value);
    clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(''), 1400);
  };

  const copyOption = async (option) => {
    if (await copyText(option)) markCopied(option);
  };

  const applyOption = async (option) => {
    if (result.move === 'finish_line' || result.move === 'punch_up') {
      if (!focusedLine) return;
      onReplaceLine(focusedLine, option);
      setUndo({ focus: focusedLine, text: focusedText });
      clearTimeout(undoTimer.current);
      undoTimer.current = window.setTimeout(() => setUndo(null), 6000);
      return;
    }
    if (result.move === 'rhymes_that_mean' && focusedLine) {
      onReplaceLine(focusedLine, swapLastWord(focusedText, option));
      return;
    }
    await copyOption(option);
  };

  const restoreOption = () => {
    if (!undo) return;
    onReplaceLine(undo.focus, undo.text);
    setUndo(null);
    clearTimeout(undoTimer.current);
  };

  const blocked = loading || !available;
  const pose = loading ? 'thinking' : result?.verdict === 'ok' ? 'satisfied' : 'confused';

  return <section className="pizzul-moves" data-testid="pizzul-moves">
    <div className="pizzul-moves-heading">
      <img src={`/pizzul/${pose}.png`} alt="Pizzul" className={loading ? 'thinking' : ''} />
      <div>
        <div className="pizzul-moves-title">
          <h2>Pizzul moves</h2>
          {Number.isFinite(Number(leftToday)) && <span>{leftToday} left today</span>}
        </div>
        <div className="pizzul-move-buttons">
          <button disabled={blocked || !focusedLine} onClick={() => beginMove('finish_line')}>
            Finish this line
          </button>
          <button
            disabled={blocked || !focusedLine || !focusedText.trim()}
            onClick={() => beginMove('punch_up')}
          >
            Punch it up
          </button>
          <button disabled={blocked || !focusedLine} onClick={openRhymes}>Rhymes that mean</button>
          <button disabled={blocked || !focusedLine} onClick={() => beginMove('stuck')}>
            I'm stuck
          </button>
        </div>
        {disabledCaption && <p className="pizzul-caption">{disabledCaption}</p>}
        {error && <p className="pizzul-error" role="alert">{error}</p>}
      </div>
    </div>

    {rhymeOpen && <form
      className="pizzul-meaning-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!targetWord.trim()) return;
        setRhymeOpen(false);
        beginMove('rhymes_that_mean', { target_word: targetWord, meaning });
      }}
    >
      <label>Rhyme with
        <input value={targetWord} onChange={(event) => setTargetWord(event.target.value)} maxLength={40} />
      </label>
      <label>About
        <input value={meaning} onChange={(event) => setMeaning(event.target.value)} maxLength={80} />
      </label>
      <div>
        <button type="button" onClick={() => setRhymeOpen(false)}>Cancel</button>
        <button type="submit" disabled={!targetWord.trim() || loading}>Ask Pizzul</button>
      </div>
    </form>}

    {result && <div className="pizzul-result">
      {result.note && <p className="pizzul-note">{result.note}</p>}
      {result.verdict === 'ok' && result.options?.length > 0 && <div className="pizzul-options">
        {result.options.map((option) => <div key={option}>
          <button onClick={() => applyOption(option)}>{option}</button>
          <button onClick={() => copyOption(option)} aria-label={`Copy ${option}`}>
            <Copy size={16} />
          </button>
        </div>)}
      </div>}
      {copied && <p className="pizzul-copied">Copied</p>}
    </div>}

    {disclosureMove && <div
      className="lyrics-ai-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booth-disclosure-title"
    >
      <div className="lyrics-ai-dialog">
        <h2 id="booth-disclosure-title">Before Pizzul jumps in</h2>
        <p>
          Pizzul's AI sends only the line you're on and a few lines above it to Anthropic,
          the company behind Claude, to come up with ideas. We don't save your lyrics.
          Keep real names, addresses and numbers out of it.
        </p>
        <div>
          <button onClick={() => setDisclosureMove(null)}>Not now</button>
          <button onClick={acceptDisclosure}>Got it</button>
        </div>
      </div>
    </div>}

    {challengeOpen && <div
      className="lyrics-ai-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booth-challenge-title"
    >
      <div className="lyrics-ai-dialog lyrics-challenge-dialog">
        <div className="lyrics-challenge-heading">
          <div>
            <h2 id="booth-challenge-title">Quick check, one sec</h2>
            <p>This keeps the room open for everybody.</p>
          </div>
          <button
            aria-label="Close quick check"
            onClick={() => {
              challengePromiseRef.current?.reject(new Error('Quick check closed.'));
              challengePromiseRef.current = null;
              setChallengeOpen(false);
            }}
          ><X size={18} /></button>
        </div>
        <div ref={challengeRef} className="lyrics-turnstile" />
        {challengeError && <p className="pizzul-error" role="alert">{challengeError}</p>}
      </div>
    </div>}

    {undo && <div className="lyrics-ai-undo" role="status">
      <span>Line changed</span>
      <button onClick={restoreOption}><Undo2 size={16} /> Undo</button>
    </div>}
  </section>;
};

export default PizzulMoves;
